import React, { Fragment, useEffect, useState } from 'react';
import GenericDivider from '../../components/GenericDivider';

import styled from 'styled-components';
import { MdCheckCircleOutline, MdHighlightOff } from 'react-icons/md';
import SearchableList from '../../components/SearchableList';
import useModalRef from '../../hooks/useModalRef';
import useToast from '../../hooks/useToast';
import useQuiz from '../../services/swr/useQuiz';
import { QuizAudit, QuizAuditGroupedByStudent } from '../../models/Quiz';
import { getLastAccessDate } from '../../utils/get-last-access-date';

interface ClassesQuizProps {
  classId: string;
}

const QuizAuditTab = ({ classId }: ClassesQuizProps) => {
  const { classQuizAudit } = useQuiz();
  const { addToast } = useToast();
  const addQuizToClassModalRef = useModalRef();
  const [quizzes, setQuizzes] = useState<QuizAuditGroupedByStudent[]>([]);

  const groupByStudent = (quizAudit: QuizAudit[]) => {
    const grouped: Record<string, QuizAuditGroupedByStudent> = {};

    for (const entry of quizAudit) {
      if (!grouped[entry.studentId]) {
        grouped[entry.studentId] = {
          studentId: entry.studentId,
          studentName: entry.studentName,
          studentEmail: entry.studentEmail,
          studentQuizzes: [],
        };
      }

      grouped[entry.studentId].studentQuizzes.push({
        quizId: entry.quizId,
        quizTitle: entry.quizTitle,
        note: entry.note,
        doneAt: entry.doneAt,
        completed: entry.completed,
        attempts: entry.attempts,
        hasPassed: entry.hasPassed,
      });
    }

    return Object.values(grouped);
  };

  const handleSearch = (search: string, quiz: QuizAuditGroupedByStudent) => {
    const treatedQuiz = Object.values(quiz)
      .join(' ')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

    const match = treatedQuiz.includes(search);

    return match;
  };

  useEffect(() => {
    try {
      classQuizAudit(classId).then(quizzes => {
        const groupedQuizzes = groupByStudent(quizzes);
        setQuizzes(groupedQuizzes);
      });
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Erro ao carregar cursos',
        description: `Não foi possível carregar os cursos da turma: ${error}`,
      });
    }
  }, [classId, classQuizAudit, addToast]);

  return (
    <>
      <SearchableList
        dataArray={quizzes}
        searchMatches={handleSearch}
        onAdd={() => addQuizToClassModalRef.current?.openModal()}
      >
        {({ data }) =>
          quizzes.length === 0 ? (
            <p>Nenhum registro de auditoria</p>
          ) : (
            <ClassCoursesListContainer>
              {data.map((quiz, index) => (
                <Fragment key={quiz.studentId}>
                  <ClassQuizListItem classId={classId} quizAudit={quiz} />
                  {index < data.length - 1 && <GenericDivider />}
                </Fragment>
              ))}
            </ClassCoursesListContainer>
          )
        }
      </SearchableList>
    </>
  );
};

export default QuizAuditTab;

interface ComponentProps {
  classId: string;
  quizAudit: QuizAuditGroupedByStudent;
}

const ClassQuizListItem = ({ quizAudit }: ComponentProps) => {
  return (
    <ListItemContainer>
      <CourseInfo>
        <p className="course-title">{quizAudit.studentName}</p>
        <p className="course-description">{quizAudit.studentEmail}</p>
      </CourseInfo>

      <QuizTable>
        <div>
          <QuizRow>
            <QuizCell bold flex={3}>
              Título do Quiz
            </QuizCell>
            <QuizCell bold flex={2}>
              Nota
            </QuizCell>
            <QuizCell bold flex={2}>
              Tentativas
            </QuizCell>
            <QuizCell bold flex={2}>
              Feito em
            </QuizCell>
            <QuizCell bold flex={1}>
              Aprovado
            </QuizCell>
          </QuizRow>
          {quizAudit.studentQuizzes.map((q, idx) => (
            <QuizRow key={idx}>
              <QuizCell flex={3} title={q.quizTitle}>
                {q.quizTitle.length > 20
                  ? `${q.quizTitle.slice(0, 20)}...`
                  : q.quizTitle}
              </QuizCell>
              <QuizCell>{q.note}</QuizCell>
              <QuizCell>{q.attempts}</QuizCell>
              <QuizCell>{getLastAccessDate(q.doneAt)}</QuizCell>
              <QuizCell>
                <StatusBadge>
                  {q.hasPassed ? (
                    <MdCheckCircleOutline size={28} color="green" />
                  ) : (
                    <MdHighlightOff size={28} color="red" />
                  )}
                </StatusBadge>
              </QuizCell>
            </QuizRow>
          ))}
        </div>
      </QuizTable>
    </ListItemContainer>
  );
};

const ClassCoursesListContainer = styled.ul`
  list-style: none;

  & > :not(:last-child) {
    margin-bottom: 24px;
  }
`;

export const ListItemContainer = styled.li`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const LabelContainer = styled.div`
  margin-top: 2rem;
  display: flex;
  align-items: center;
  justify-content: start;
`;

export const CourseInfo = styled.article`
  width: 100%;
  padding-left: 0.5rem;
  margin-bottom: 1.5rem;

  .course-title,
  .course-description {
    font-family: var(--font-sans);
  }

  .course-title {
    font-size: 1.25rem;
    font-weight: 500;
  }
`;

export const QuizTable = styled.div`
  display: flex;
  padding-top: 0.5rem;
  flex-direction: column;
  background-color: #f0fff0e2;
  width: 100%;
  gap: 0.5rem;
  border-radius: 8px;

  @media screen and (mad-width: 768px) {
    flex-direction: row;
  }
`;

export const QuizRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

export const QuizCell = styled.span<{ flex?: number; bold?: boolean }>`
  font-weight: ${({ bold }) => (bold ? 'bold' : 'normal')};
  //flex: ${({ flex = 1 }) => flex};
  display: flex;
  min-width: 13rem;
  align-items: center;
  justify-content: center;
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
`;

export const StatusBadge = styled.span`
  padding: 0.2rem 0.5rem;
  border-radius: 4px;

  color: white;
  font-size: 0.75rem;
`;
